import { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { Button, Form, Input, Space, Table, Modal, Typography, message, Divider } from 'antd';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { savePartnersApi, updatePartnersApi, savePartnerUsersApi, getPartnerUsersApi, updatePartnerUsersApi, partnersApi } from '@services/ServiceProvider';
import PageHeader from '@components/PageHeader/PageHeader';
import './index.less';
const { Text } = Typography;
const { Search } = Input;

export default function ServiceProvider() {
    const [serviceProviderForm] = Form.useForm();
    const [addPersonForm] = Form.useForm();
    const [resetPasswordForm] = Form.useForm();

    // 添加新的状态用于管理弹框
    const [isAddServiceProviderOpen, setIsAddServiceProviderOpen] = useState(false);
    const [isManageServiceProviderOpen, setIsManageServiceProviderOpen] = useState(false);
    const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
    const [addPersonModalVisible, setAddPersonModalVisible] = useState(false);
    const [curEditPerson, setCurEditPerson] = useState(undefined);
    const [currentServiceProvider, setCurrentServiceProvider] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [filter, setFilter] = useState({ name: '' });
    const [searchName, setSearchName] = useState('');

    const [tableData, setTableData] = useState([]);
    const [personTableData, setPersonTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const columns = [
        {
            title: '服务商ID',
            dataIndex: 'id',
            width: 80,
        },
        {
            title: '服务商',
            dataIndex: 'name',
        },
        {
            title: '服务人员',
            dataIndex: 'userCount',
            width: 200,
            render: (_, row) => (
                <>
                    <span style={{ display: 'inline-block', width: 30, marginRight: 8 }}>{!!row.userCount ? `${row.userCount}人` : '0人'}</span>
                    <Button type="link" size="small" onClick={() => manageServiceProvider(row)}>
                        管理服务人员
                    </Button>
                </>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            align: 'center',
            render: (_, record) => {
                return (
                    <>
                        {record.status === 0 && <Text type="danger">正常</Text>}
                        {record.status === 1 && <Text type="success">冻结</Text>}
                    </>
                );
            },
        },
        {
            title: '操作',
            dataIndex: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => {
                const isFrozen = record.status === 1;
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button type="link" size="small" onClick={() => editServiceProvider(record)}>
                            编辑
                        </Button>
                        <Button type="link" size="small" onClick={() => toggleFreezeServiceProvider(record)}>
                            {isFrozen ? '解冻' : '冻结'}
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const personColumns = [
        {
            title: '姓名',
            dataIndex: 'name',
            width: 100,
        },
        {
            title: '账号',
            dataIndex: 'phone',
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 80,
            align: 'center',
            render: status => {
                return (
                    <>
                        {status === 0 && <Text type="success">正常</Text>}
                        {status === 1 && <Text type="danger">冻结</Text>}
                    </>
                );
            },
        },
        {
            title: '操作',
            dataIndex: 'action',
            width: 240,
            render: (_, record) => {
                const isFrozen = record.status === 1;
                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button type="link" size="small" onClick={() => editPerson(record)}>
                            编辑
                        </Button>
                        <Button type="link" size="small" onClick={() => freezePerson(record)}>
                            {isFrozen ? '解冻' : '冻结'}
                        </Button>
                        <Button type="link" size="small" onClick={() => resetPersonPassword(record)}>
                            重置密码
                        </Button>
                    </Space>
                );
            },
        },
    ];

    const onSearchName = value => {
        setFilter({
            name: value || '',
        });
    };

    const handleOk = async () => {
        try {
            const values = await serviceProviderForm.validateFields();

            if (isEditMode) {
                const res = await updatePartnersApi({
                    id: currentServiceProvider.id,
                    name: values.name,
                });
                if (res.status_code === 200) {
                    message.success('编辑服务商成功');
                }
            } else {
                const res = await savePartnersApi({
                    name: values.name,
                });
                if (res.status_code === 200) {
                    message.success('新增服务商成功');
                }
            }

            setIsAddServiceProviderOpen(false);
            serviceProviderForm.resetFields();
            loadServiceProviders();
        } catch (errorInfo) {
            console.log('校验失败:', errorInfo);
        }
    };

    const handleCancel = () => {
        setIsAddServiceProviderOpen(false);
        serviceProviderForm.resetFields();
        setCurrentServiceProvider(null);
    };

    const handleAddPersonOk = async () => {
        try {
            const values = await addPersonForm.validateFields();

            if (curEditPerson) {
                const res = await updatePartnerUsersApi({
                    id: curEditPerson.id,
                    name: values.name,
                    phone: values.phone,
                    partnerId: currentServiceProvider.id,
                });
                if (res.status_code === 200) {
                    message.success('编辑人员成功');
                    loadPersonList();
                    setAddPersonModalVisible(false);
                    return;
                }
            } else {
                const res = await savePartnerUsersApi({
                    name: values.name,
                    phone: values.phone,
                    password: values.password,
                    partnerId: currentServiceProvider.id,
                });
                if (res.status_code === 200) {
                    message.success('新增人员成功');
                    setAddPersonModalVisible(false);
                    addPersonForm.resetFields();
                    setCurEditPerson(undefined);
                    loadPersonList();
                    loadServiceProviders();
                }
            }
        } catch (errorInfo) {
            console.log('校验失败:', errorInfo);
        }
    };

    const handleAddPersonCancel = () => {
        setAddPersonModalVisible(false);
        addPersonForm.resetFields();
        setCurEditPerson(undefined);
    };

    // 冻结/解冻服务商
    const toggleFreezeServiceProvider = record => {
        const isFrozen = record.status === 1;

        Modal.confirm({
            title: '提示',
            content: isFrozen ? '解冻后，该服务商下所有人员将恢复查看已授权项目' : '冻结后，该服务商下所有人员将无法查看已授权项目',
            async onOk() {
                try {
                    const res = await updatePartnersApi({
                        id: record.id,
                        status: isFrozen ? 0 : 1,
                    });
                    if (res.status_code === 200) {
                        message.success(isFrozen ? '解冻成功' : '冻结成功');
                        loadServiceProviders();
                    }
                } catch {
                    message.error(isFrozen ? '冻结失败' : '解冻失败');
                }
            },
        });
    };

    // 管理服务商人员
    const manageServiceProvider = async record => {
        setCurrentServiceProvider(record);
        setIsManageServiceProviderOpen(true);
        await loadPersonList(record.id);
    };

    // 新增服务商弹框打开
    const addServiceProvider = () => {
        serviceProviderForm.resetFields();
        setIsEditMode(false);
        setCurrentServiceProvider(null);
        setIsAddServiceProviderOpen(true);
    };

    // 编辑服务商
    const editServiceProvider = record => {
        serviceProviderForm.setFieldsValue({ name: record.name });
        setIsEditMode(true);
        setCurrentServiceProvider(record);
        setIsAddServiceProviderOpen(true);
    };

    // 新增人员
    const addPerson = () => {
        addPersonForm.resetFields();
        setCurEditPerson(undefined);
        setAddPersonModalVisible(true);
    };

    // 编辑人员
    const editPerson = record => {
        addPersonForm.setFieldsValue(record);
        setAddPersonModalVisible(true);
        setCurEditPerson(record);
    };

    // 冻结人员
    const freezePerson = record => {
        const isFrozen = record.status === 1;

        Modal.confirm({
            title: '提示',
            content: isFrozen ? '解冻后，该服务商人员将恢复查看已授权项目' : '冻结后，该服务商人员将无法查看已授权项目',
            async onOk() {
                try {
                    const res = await updatePartnerUsersApi({
                        id: record.id,
                        status: isFrozen ? 0 : 1,
                    });
                    if (res.status_code === 200) {
                        message.success(isFrozen ? '解冻成功' : '冻结成功');
                        loadPersonList();
                    }
                } catch {
                    message.error(isFrozen ? '解冻失败' : '冻结失败');
                }
            },
        });
    };

    // 重置密码
    const resetPersonPassword = record => {
        resetPasswordForm.setFieldsValue({
            partnerName: currentServiceProvider?.name,
            name: record.name,
        });
        setCurEditPerson(record);
        setResetPasswordModalVisible(true);
    };

    const handleResetPasswordOk = async () => {
        try {
            const values = await resetPasswordForm.validateFields();
            const res = await updatePartnerUsersApi({
                id: curEditPerson.id,
                password: values.password,
            });
            if (res.status_code === 200) {
                message.success('重置密码成功');
                setResetPasswordModalVisible(false);
                resetPasswordForm.resetFields();
                setCurEditPerson(undefined);
            }
        } catch (errorInfo) {
            console.log('校验失败:', errorInfo);
        }
    };

    const handleResetPasswordOkCancel = () => {
        setResetPasswordModalVisible(false);
        resetPasswordForm.resetFields();
        setCurEditPerson(undefined);
    };

    // 加载服务商列表
    const loadServiceProviders = async () => {
        setLoading(true);
        try {
            const res = await partnersApi({
                name: filter.name,
            });
            if (res.status_code === 200 && res.data) {
                setTableData(res.data || []);
            }
        } catch (error) {
            console.error('加载服务商列表失败:', error);
            message.error('加载服务商列表失败');
        } finally {
            setLoading(false);
        }
    };

    // 加载人员列表
    const loadPersonList = async partnerId => {
        const id = partnerId || currentServiceProvider?.id;
        if (!id) return;

        try {
            const res = await getPartnerUsersApi({ id });
            if (res.status_code === 200 && res.data) {
                setPersonTableData(res.data || []);
            }
        } catch (error) {
            console.error('加载人员列表失败:', error);
            message.error('加载人员列表失败');
        }
    };

    const handleRefresh = () => loadServiceProviders();

    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        loadServiceProviders();
    }, [filter]);

    useEffect(() => {
        document.title = '服务商管理-医会议';
    }, []);

    return (
        <div className="page-container service-provider-page">
            <PageHeader title="服务商管理" breadcrumb={[{ label: '医会议', path: '/conf/conferences' }, { label: '服务商管理' }]} />

            <div ref={tableRegionRef} className="table-region-container service-provider-table-region">
                <div className="main-toolbar service-provider-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={addServiceProvider}>
                                新增服务商
                            </Button>
                            <Search placeholder="服务商名称关键字" allowClear style={{ width: 250 }} value={searchName} onChange={event => setSearchName(event.target.value)} onSearch={onSearchName} />
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Button type="default" onClick={handleRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap service-provider-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        bordered
                        sticky
                        size="medium"
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        pagination={false}
                    />
                </div>
            </div>

            {/* 服务商弹框 - 编辑和新增共用 */}
            <Modal title={isEditMode ? '编辑服务商' : '新增服务商'} open={isAddServiceProviderOpen} onOk={handleOk} onCancel={handleCancel} width={500} centered className="service-provider-modal">
                <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={serviceProviderForm} autoComplete="off">
                    <Form.Item name="name" label="服务商名称" rules={[{ required: true, message: '请输入服务商名称' }]}>
                        <Input allowClear placeholder="请输入服务商名称" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 管理服务商人员弹框 */}
            <Modal title="管理服务商人员" open={isManageServiceProviderOpen} onCancel={() => setIsManageServiceProviderOpen(false)} width={800} centered className="manage-service-provider" footer={null}>
                <Form labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} form={serviceProviderForm} autoComplete="off">
                    <Form.Item label="服务商名称">
                        <span>{currentServiceProvider?.name || ''}</span>
                    </Form.Item>
                    <Form.Item label="服务商人员">
                        <Button className="add-person-btn" type="link" onClick={addPerson} icon={<i className="bi bi-plus"></i>}>
                            新增人员
                        </Button>
                        <Table rowKey="id" columns={personColumns} dataSource={personTableData} loading={loading} pagination={false} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title={curEditPerson ? '编辑人员' : '新增人员'} open={addPersonModalVisible} onOk={handleAddPersonOk} onCancel={handleAddPersonCancel} width={600} centered className="add-service-provider">
                <Form labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} form={addPersonForm} autoComplete="off">
                    <Form.Item name="name" label="姓名">
                        <Input allowClear placeholder="姓名" />
                    </Form.Item>
                    <Form.Item name="phone" label="登录账号">
                        <Input allowClear placeholder="请输入手机号" />
                    </Form.Item>
                    {!curEditPerson && (
                        <Form.Item
                            name="password"
                            label="登录密码"
                            extra="密码不少于6位，必须包含数字或字母（大写或小写）"
                            rules={[
                                { required: true, message: '请输入密码' },
                                { pattern: /^(?=.*[a-zA-Z0-9]).{6,}$/, message: '密码不少于6位，必须包含数字或字母（大写或小写）' },
                            ]}
                        >
                            <Input.Password allowClear placeholder="请输入密码" />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal title="重置密码" open={resetPasswordModalVisible} onOk={handleResetPasswordOk} onCancel={handleResetPasswordOkCancel} width={600} centered className="add-service-provider">
                <Form labelCol={{ span: 4 }} wrapperCol={{ span: 20 }} form={resetPasswordForm} autoComplete="off">
                    <Form.Item name="partnerName" label="所属服务商">
                        <span>{currentServiceProvider?.name}</span>
                    </Form.Item>
                    <Form.Item name="name" label="姓名">
                        <span>{curEditPerson?.name}</span>
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="新密码"
                        extra="密码不少于6位，必须包含数字或字母（大写或小写）"
                        rules={[
                            { required: true, message: '请输入新密码' },
                            { pattern: /^(?=.*[a-zA-Z0-9]).{6,}$/, message: '密码不少于6位，必须包含数字或字母（大写或小写）' },
                        ]}
                    >
                        <Input.Password allowClear placeholder="请输入新密码" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
